import { logger } from "../logger";
import { sendCapiEvent } from "./meta-capi";

/**
 * Meta (Facebook) Pixel wrapper.
 *
 * Mirrors the PostHog wrapper in analytics.ts: the whole module is gated on
 * VITE_META_PIXEL_ID. With no id configured it is a complete no-op, so the app
 * runs identically in dev or before the pixel is set up. Set VITE_META_PIXEL_ID
 * in the environment to switch it on — nothing else needs to change, because
 * telemetry.track() already routes every funnel event through captureMetaEvent().
 *
 * Only the funnel/conversion events in FUNNEL_EVENT_MAP are forwarded to Meta;
 * the dozens of diagnostic events the app fires are ignored here so the pixel
 * stays clean and useful for ad optimization. No custom properties are sent —
 * the event name is all Meta needs to optimize, and it keeps user data off the
 * pixel. A per-event eventID is attached so a future Conversions API (CAPI) call
 * can be de-duplicated against the browser event.
 */

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

/** Map internal funnel event names → Meta event names. */
interface MetaEventMapping {
  /** The Meta event name to fire. */
  name: string;
  /** true = a Meta "standard" event (fbq track); false = a custom event. */
  standard: boolean;
}

const FUNNEL_EVENT_MAP: Record<string, MetaEventMapping> = {
  // Top of funnel — the primary optimization event for cold prospecting ads.
  assessment_started: { name: "AssessmentStarted", standard: false },
  step_completed: { name: "AssessmentStepCompleted", standard: false },
  // Mid funnel — someone saw their real, calculated results.
  detailed_results_viewed: { name: "ViewContent", standard: true },
  // Conversions — the events an ad account should ultimately optimize toward.
  consultation_requested: { name: "ConsultationRequested", standard: false },
  assessment_contact_saved: { name: "Lead", standard: true },
  lead_created: { name: "Lead", standard: true },
};

/** Minimal typing for the global fbq queue the Meta snippet installs. */
interface FbqFn {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: FbqFn;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

let initialized = false;

/** The standard Meta Pixel bootstrap, typed and without the minified snippet. */
function loadPixelScript(): void {
  if (window.fbq) return;

  const fbq = function (...args: unknown[]): void {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqFn;

  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";

  window.fbq = fbq;
  if (!window._fbq) {
    window._fbq = fbq;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
}

function newEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** Initialize the Meta Pixel once, on the client, only when an id is configured. */
export function initMetaPixel(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (!META_PIXEL_ID) return;

  try {
    loadPixelScript();
    // Turn off Automatic Events (button-click text and page metadata) before
    // init: assessment answers are buttons, and the privacy policy says the
    // Pixel does not collect them. Must run before "init" to take effect.
    window.fbq?.("set", "autoConfig", false, META_PIXEL_ID);
    window.fbq?.("init", META_PIXEL_ID);
    window.fbq?.("track", "PageView");
    initialized = true;
  } catch (error) {
    logger.error("Failed to initialize Meta Pixel", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Forward a funnel event to Meta. No-op until initMetaPixel() has run with an id,
 * and silently ignores any event not in FUNNEL_EVENT_MAP.
 *
 * Fires the browser pixel and mirrors the event to the Conversions API using
 * ONE shared eventID, so Meta de-duplicates the browser + server copies. The
 * CAPI mirror is itself a no-op unless VITE_META_CAPI_URL is configured.
 */
export function captureMetaEvent(
  internalName: string,
  canonicalEventId?: string,
): void {
  if (!initialized || !window.fbq) return;

  const mapping = FUNNEL_EVENT_MAP[internalName];
  if (!mapping) return;

  const eventId = canonicalEventId ?? newEventId();
  const method = mapping.standard ? "track" : "trackCustom";
  window.fbq(method, mapping.name, {}, { eventID: eventId });
  sendCapiEvent(mapping.name, eventId);
}

export function isMetaPixelInitialized(): boolean {
  return initialized;
}
